// What a non-medication care-plan task actually IS, for display.
//
// CarePlanTasks.task_type is the care plan's TEMPLATE name, not the order:
// manage_care_plan.py creates every generated task with
// task_type=care_plan.name, so everything filed under the "Task" care plan
// carries the literal string "Task". The doctor's own words live in the
// care-plan payload, which nurse_dashboard_tasks/ serves as order_details.
// (care_plan_tasks/ folds those fields into a `compact_detail` string for
// exactly this reason; nurse_dashboard_tasks/ does not, so the nurse
// dashboard has to read order_details itself.)
//
// The keys below are the care-plan fields the backend documents in
// CARE_PLAN_STRUCTURE_MAP (manage_care_plan_task.py) — the title is whichever
// "what is it" field a plan carries, the detail lines are the shared context
// fields every plan shares.
import { isMedicationTask } from "./medicationDisplay";

// In backend field order: task/appointment carry `description`, test carries
// `orderDetail`, discharge advice `notes`, and notes-type plans only `reason`.
const TITLE_KEYS = ["description", "orderDetail", "notes", "reason"];

const DETAIL_FIELDS = [
  ["priority", "Priority"],
  ["intent", "Intent"],
  ["category", "Category"],
  ["specialty", "Specialty"],
  ["reason", "Reason"],
];

// Only scalars are usable as text. Some of these fields hold objects on some
// care plans (a test's orderDetail, a medication block), which would otherwise
// render as "[object Object]".
const textOf = (v) =>
  typeof v === "string" || typeof v === "number" ? String(v).trim() : "";

// The doctor's description of the task, or "" when there is none to show.
// Medication doses are deliberately excluded: they are named off the drug
// (see medicationDisplay.js), and their care plan carries a `reason` that
// would otherwise hijack the row heading.
export function orderTitle(task) {
  if (isMedicationTask(task)) return "";
  const details = task?.order_details || {};
  for (const key of TITLE_KEYS) {
    const value = textOf(details[key]);
    if (value) return value;
  }
  return "";
}

// The heading for a task, in order of how specific it is: what the doctor
// wrote, else the name of the form the nurse is being handed ("Wound
// Dressing"), else the care plan's template name — which names nothing, so it
// is the last resort. fulfillmentNames comes from useFulfillmentNames().
export function taskHeading(task, fulfillmentNames = {}) {
  return (
    orderTitle(task) ||
    fulfillmentNames[task?.fulfillment_type_id] ||
    task?.task_type ||
    "Task"
  );
}

// The rest of what was ordered, as "Priority: routine" lines — minus whatever
// already became the title, so the modal never says the same thing twice.
export function orderDetailLines(task) {
  const details = task?.order_details || {};
  const title = orderTitle(task);
  return DETAIL_FIELDS.map(([key, label]) => [label, textOf(details[key])])
    .filter(([, value]) => value && value !== title)
    .map(([label, value]) => `${label}: ${value}`);
}
